import type { ReactNode } from 'react';
import type { ApprovalRoutePreviewStep, RequisitionDetail, RequisitionRow } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { useRoutePreview } from '../api/requisitions-api';
import styles from './WhatHappensNext.module.css';

// REQ-01 / HM-03, bug B8 (§L41): "What happens next" shows the route the request really takes — the HR check first, then the
// department's approval chain or Leadership — read from the server, so it never promises a step that isn't there.

interface RouteStep {
  readonly key: string;
  readonly title: string;
  readonly detail: string;
}

/** What the requester reads for the HR check. The Jev budget is never mentioned: a requester doesn't see it (ADR 0086 §4). */
const HR_CHECK: RouteStep = { key: 'hr-check', title: 'HR checks it', detail: 'Someone in HR reads it and can fix anything unclear.' };

const JOB_MADE: RouteStep = { key: 'job', title: 'The job is made for you', detail: 'A draft job appears in Jobs at once, filled from this request.' };

/** "Any Leadership. The first to act decides." · "Mahbub Alam." · "Mahbub Alam and Any Leadership. All must approve." */
function approversText(approvers: ApprovalRoutePreviewStep['approvers']): string {
  const names = approvers.map((approver) => approver.label);
  const joined = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}` : (names[0] ?? 'An approver');
  if (approvers.length > 1) return `${joined}. All must approve.`;
  return approvers[0]?.isRole ? `${joined}. The first to act decides.` : `${joined}.`;
}

function fromPreview(step: ApprovalRoutePreviewStep): RouteStep {
  return step.isHrCheck ? HR_CHECK : { key: `step-${step.stepNumber}`, title: step.name, detail: approversText(step.approvers) };
}

/** HR at the check: the request's own steps, as they were copied onto it when it was sent. */
function stepsOfRequest(detail: RequisitionDetail): readonly RouteStep[] {
  const round = detail.approvals.filter((step) => step.requisitionVersion === detail.currentVersion);
  const numbers = [...new Set(round.map((step) => step.stepNumber))].sort((a, b) => a - b);
  return numbers.map((number) => {
    const group = round.filter((step) => step.stepNumber === number);
    const first = group[0];
    const names = group.map((step) => step.approverName);
    const waiting = group.some((step) => step.status === 'pending' && step.activatedAt !== null) ? ' Waiting now.' : '';
    return { key: `step-${number}`, title: first?.name ?? '', detail: `${names.join(', ')}.${group.length > 1 ? ' All must approve.' : ''}${waiting}` };
  });
}

export function WhatHappensNext({
  departmentId,
  check,
}: {
  /** The department the request is for; null until a position or department is chosen. */
  readonly departmentId: string | null;
  /** HR at the check: the request already sent, whose steps are shown instead. */
  readonly check: RequisitionDetail | null;
}): ReactNode {
  const preview = useRoutePreview(check ? null : departmentId);
  const known = check ? stepsOfRequest(check) : preview.data ? preview.data.steps.map(fromPreview) : null;
  // Until the server answers, the HR check is already certain: it comes first on every route.
  const steps: readonly RouteStep[] = known ?? [HR_CHECK, { key: 'pending', title: 'Then the approvers for this department', detail: pendingText(departmentId, preview.isError) }];
  const chainName = check ? null : (preview.data?.chainName ?? null);
  return (
    <Card title="What happens next">
      <ol className={styles.flow} aria-busy={!check && departmentId !== null && preview.isFetching ? true : undefined}>
        {[...steps, JOB_MADE].map((step, index) => (
          <li key={step.key}>
            <span className={styles.number}>{index + 1}</span>
            <div>
              <b>{step.title}</b>
              <span>{step.detail}</span>
            </div>
          </li>
        ))}
      </ol>
      {chainName && <p className={styles.small}>From the approval chain “{chainName}”.</p>}
      <p className={styles.small}>Nobody approves their own request.</p>
    </Card>
  );
}

function pendingText(departmentId: string | null, isError: boolean): string {
  if (departmentId === null) return 'Choose the position or department to see who approves after HR.';
  if (isError) return "Who approves after HR couldn't be loaded. You'll see it on the request once it's sent.";
  return 'Finding who approves after HR…';
}

/** The toast's line after sending: the steps the server gave the request, in order ("HR check → Department head → Finance approval"). */
export function routeSentence(track: RequisitionRow['approvalTrack']): string | undefined {
  return track.length > 0 ? track.map((step) => step.name).join(' → ') : undefined;
}
